<template>
  <v-container class="py-0" fluid data-qa="list_selector">
    <v-row v-if="searchable" justify="center" class="list-row">
      <v-col cols="10">
        <bm-text
          v-model="filterString"
          :t="searchTitleTranslationKey"
          prepend-icon="search"
          clearable
          class="ma-0"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="pa-0">
        <v-list class="pa-0">
          <v-list-item-group
            v-model="selectedListItemIndexes"
            :multiple="showSelect"
          >
            <v-list-item
              v-for="item in filteredListItems"
              :key="item.key"
              v-slot="{ active }"
              class="list-row"
              :class="{ 'py-1': large }"
              data-qa="list-row"
              :two-line="item.subtitle && item.text !== undefined"
              :three-line="item.text !== undefined"
              @click="processClick(item)"
            >
              <v-list-item-action v-if="showSelect">
                <v-checkbox :input-value="active" />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title data-qa="list-row-title">
                  {{ item.title }}
                </v-list-item-title>
                <v-list-item-subtitle
                  v-if="item.subtitle !== undefined"
                  data-qa="list-row-subtitle"
                >
                  {{ item.subtitle }}
                </v-list-item-subtitle>
                <v-list-item-subtitle
                  v-if="item.text !== undefined"
                  data-qa="list-row-text"
                >
                  {{ item.text }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action
                v-if="showStatus && item.status !== undefined"
              >
                <v-icon
                  v-if="item.status !== EnumListItemStatus.none"
                  :data-qa="item.status.value"
                  :class="[item.status.value, item.status.className]"
                />
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-col>
    </v-row>
  </v-container>
</template>
<script lang="ts">
import type { PropType } from "vue";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import type { IListItem } from "@/components/controls/listselector/BmListTypes";
import { EnumListItemStatus } from "@/components/controls/listselector/BmListTypes";
import { keys } from "@/plugins/i18n";

/**
 * A basic list builder, built using IListItems.
 *
 * Supports actions and 3-values.
 */

export default defineComponent({
  name: "BmList",
  data: () => {
    return { EnumListItemStatus };
  },
  props: {
    items: {
      type: Array as PropType<IListItem[]>,
      required: true,
    },
    showStatus: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchable: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchTitleTranslationKey: {
      type: Object,
      required: false,
      default: () => keys.search,
    },
    large: {
      type: Boolean,
      required: false,
      default: false,
    },
    showSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedItemKeys: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => [],
    },
    initialSelectAll: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, context) {
    const filterString = ref<string>("");
    const selectedListItemIndexes = ref<number[]>([]);

    onMounted(() => {
      if (props.selectedItemKeys.length) {
        // if we have selected item keys, find the index of each key and mark
        // as selected
        selectedListItemIndexes.value = filteredListItems.value.reduce(
          (acc, curr, idx) => {
            if (props.selectedItemKeys.includes(curr.key)) {
              acc.push(idx);
            }

            return acc;
          },
          [] as number[]
        );
        return;
      }

      if (props.initialSelectAll) {
        // set all items as selected initially
        selectedListItemIndexes.value = filteredListItems.value.map(
          (_, idx) => idx
        );
      }
    });

    watch(
      selectedListItemIndexes,
      () => {
        if (!props.showSelect) {
          return;
        }

        const selectedItems = filteredListItems.value.filter((_, idx) =>
          selectedListItemIndexes.value.some((val) => idx === val)
        );

        context.emit(
          "update:selected-item-keys",
          selectedItems.map((item) => item.key)
        );
      },
      { deep: true }
    );

    // Handle the click action if one is provided
    const processClick = (item: IListItem) => {
      if (item.disabled || item.action === undefined) {
        return;
      }

      item.action();
    };

    const filteredListItems = computed(() => {
      return props.items.filter((item) => {
        return (
          item.title
            .toLocaleLowerCase()
            .includes(filterString.value.toLocaleLowerCase()) ||
          item.subtitle
            ?.toLocaleLowerCase()
            .includes(filterString.value.toLocaleLowerCase()) ||
          item.text
            ?.toLocaleLowerCase()
            .includes(filterString.value.toLocaleLowerCase())
        );
      });
    });

    return {
      processClick,
      filterString,
      filteredListItems,
      selectedListItemIndexes,
    };
  },
});
</script>
<style lang="sass" scoped>
@import "src/sass/colours"

.list-row
  border-bottom: 1px solid $bm-color-borders
  color: $bm-color-text01
  cursor: pointer

:deep( .v-list-item__action)
  align-self: normal
</style>
